/** Client-side overlay-vs-overlay diff for the Versions tab detail panel
 * (design spec §9). Overlays are small keyed dicts — walk every section's
 * keys and classify `section.key` refs. */
import type { WorkflowOverlayJson } from '@/types/workflowConfig'

export interface OverlayRefDiff {
  added: string[]
  removed: string[]
  changed: string[]
}

/** Order-insensitive stringify for the "changed" check — historical
 * overlay_snapshots were constructed at different times, so two
 * semantically-identical entries can differ only in object key order.
 * Array order is still meaningful in overlays, so arrays are NOT sorted. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
    return `{${entries.join(',')}}`
  }
  return JSON.stringify(value)
}

export function diffOverlayRefs(
  target: WorkflowOverlayJson | null,
  current: WorkflowOverlayJson | null,
): OverlayRefDiff {
  const a = (target ?? {}) as Record<string, unknown>
  const b = (current ?? {}) as Record<string, unknown>
  const added: string[] = []
  const removed: string[] = []
  const changed: string[] = []
  const sections = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const section of [...sections].sort()) {
    const sa = a[section]
    const sb = b[section]
    const ka = sa && typeof sa === 'object' ? Object.keys(sa as object) : []
    const kb = sb && typeof sb === 'object' ? Object.keys(sb as object) : []
    for (const key of [...new Set([...ka, ...kb])].sort()) {
      const ref = `${section}.${key}`
      const va = (sa as Record<string, unknown> | undefined)?.[key]
      const vb = (sb as Record<string, unknown> | undefined)?.[key]
      if (va !== undefined && vb === undefined) added.push(ref)
      else if (va === undefined && vb !== undefined) removed.push(ref)
      else if (stableStringify(va) !== stableStringify(vb)) changed.push(ref)
    }
  }
  return { added, removed, changed }
}
